import React, { useCallback } from 'react';
import {
  Box,
  Button,
  ButtonSelect,
  MenuMulti,
  Typography,
} from '@saviynt/design-system';

import type { MenuMultiOption } from '@saviynt/design-system';

import { useInvertedLocalize } from '../../../utils/localization';

import invertedMessages from './messages';

import './DashboardFilterPills.css';

interface FilterOption {
  label: string;
  value: string;
}

interface DashboardFilterPillsProps {
  isVisible: boolean;
  activeFilters: {
    applications: string[];
    entitlements: string[];
  };
  availableFilters: {
    applications: FilterOption[];
    entitlements: FilterOption[];
  };
  isLoading: boolean;
  onClearAll: () => void;
  onSetFilter: (
    category: 'applications' | 'entitlements',
    values: string[]
  ) => void;
}

function DashboardFilterPills({
  isVisible,
  activeFilters,
  availableFilters,
  isLoading,
  onClearAll,
  onSetFilter,
}: DashboardFilterPillsProps): JSX.Element {
  const l = useInvertedLocalize(invertedMessages);

  const hasActiveFilters =
    activeFilters.applications.length > 0 ||
    activeFilters.entitlements.length > 0;

  const handleApplicationsChange = useCallback(
    (selectedOptions?: MenuMultiOption[]) => {
      if (!selectedOptions) {
        return;
      }

      const selectedValues = selectedOptions.map((option) => option.value);

      onSetFilter('applications', selectedValues);
    },
    [onSetFilter]
  );

  const handleEntitlementsChange = useCallback(
    (selectedOptions?: MenuMultiOption[]) => {
      if (!selectedOptions) {
        return;
      }

      const selectedValues = selectedOptions.map((option) => option.value);

      onSetFilter('entitlements', selectedValues);
    },
    [onSetFilter]
  );

  return (
    <Box
      className={`Roles-DashboardFilterPills ${
        isVisible
          ? 'Roles-DashboardFilterPills--isVisible'
          : 'Roles-DashboardFilterPills--isHidden'
      }`}
      aria-hidden={!isVisible}>
      {isLoading ? (
        <Typography kind='body2' className='Roles-DashboardFilterPills-loading'>
          {l('Loading filters...')}
        </Typography>
      ) : (
        <>
          {/* Filter Selection Menus */}
          <Box className='Roles-DashboardFilterPills-menus'>
            {availableFilters.applications.length > 0 && (
              <Box className='Roles-DashboardFilterPills-menu'>
                <MenuMulti
                  options={availableFilters.applications.map((option) => ({
                    ...option,
                    isChecked: activeFilters.applications.includes(
                      option.value
                    ),
                  }))}
                  label={l('Applications')}
                  onChange={handleApplicationsChange}
                  trigger={
                    <ButtonSelect kind='pill' label={l('Applications')} />
                  }
                  size='medium'
                  isPortalEnabled
                  classNames={{
                    dropdownClassName:
                      'Roles-DashboardFilterPills-menuMultiDropdown',
                  }}
                />
              </Box>
            )}

            {availableFilters.entitlements.length > 0 && (
              <Box className='Roles-DashboardFilterPills-menu'>
                <MenuMulti
                  options={availableFilters.entitlements.map((option) => ({
                    ...option,
                    isChecked: activeFilters.entitlements.includes(
                      option.value
                    ),
                  }))}
                  label={l('Entitlements')}
                  onChange={handleEntitlementsChange}
                  trigger={
                    <ButtonSelect kind='pill' label={l('Entitlements')} />
                  }
                  size='medium'
                  isPortalEnabled
                  classNames={{
                    dropdownClassName:
                      'Roles-DashboardFilterPills-menuMultiDropdown',
                  }}
                />
              </Box>
            )}

            {hasActiveFilters && (
              <Button
                className='Roles-DashboardFilterPills-clearAll'
                onClick={onClearAll}
                kind='ghost'
                size='small'>
                {l('Clear')}
              </Button>
            )}
          </Box>

          {availableFilters.applications.length === 0 &&
            availableFilters.entitlements.length === 0 && (
              <Typography
                kind='body2'
                className='Roles-DashboardFilterPills-empty'>
                {l('No filters available or they failed to load.')}
              </Typography>
            )}
        </>
      )}
    </Box>
  );
}

export default DashboardFilterPills;
